import React from 'react';
import { Card, Flex } from '@mantine/core';
import { ActiveGoalsEntity } from 'types/goals';
import {
  Separator,
  bottomBannerStyle,
  cursorPointer,
  fontWeight,
  giveToGoalBtnStyle,
  giveToGoalBtnStyleMobile,
  goalCardStyle,
  paddingLeft,
  supportButtonStyle,
} from './styles';
import { getGoalIcon } from 'utils/getGoalIcon';
import {
  Button,
  Colors,
  Icon,
  IconName,
  Text,
  TypesButton,
  Typography,
} from 'goalsetter-webapp-components';
import { t } from 'i18next';
import { GsDate, formatToCurrency } from 'utils';
import { useMediaQueryGs } from 'app/hooks/useMediaQuery';

interface GoalCardDetailsProps {
  goal: ActiveGoalsEntity;
  singleCard?: boolean;
  selection: (value: ActiveGoalsEntity) => void;
  goToSelectCard: () => void;
}

export const GoalCardDetails: React.FC<GoalCardDetailsProps> = ({
  goal,
  selection,
  singleCard,
  goToSelectCard,
}) => {
  const { isMobile } = useMediaQueryGs();

  const progressPercentage = goal?.amount
    ? Math.round((goal.publicAchievedAmount / goal.amount) * 100)
    : 0;
  const hasInvestors = Boolean(
    goal.investors?.length && goal.investors?.length > 0,
  );
  const date = goal.enddate && GsDate(goal.enddate).format('MM/DD/YYYY');

  // hide the amount but show the percentage
  const showPercentageOnly = goal.visibilityType === 1 && Boolean(goal.amount);
  // hide values
  const hideValues = goal.visibilityType === 1 && !Boolean(goal.amount);

  const handleSelection = () => selection(goal);

  return (
    <Card
      radius="lg"
      style={goalCardStyle(singleCard)}
    >
      <Flex
        justify="space-between"
        align="center"
        onClick={!singleCard ? handleSelection : undefined}
        style={!singleCard ? cursorPointer : undefined}
      >
        <Flex
          align="center"
          gap="16px"
        >
          {getGoalIcon(goal.type, '40px')}
          <Text type={Typography.GSLINK}>{goal.prettyName}</Text>
        </Flex>
        {!singleCard && (
          <Icon
            icon={IconName.CHEVRON_RIGHT}
            size={40}
            color={Colors.BLUE_LIGHT}
            onClick={handleSelection}
            style={cursorPointer}
          />
        )}
      </Flex>
      <Separator
        value={progressPercentage}
        color={
          progressPercentage && progressPercentage >= 100
            ? Colors.GREEN_LIGHT
            : Colors.ORANGE
        }
      />

      <Flex direction="column">
        {showPercentageOnly && (
          <Text type={Typography.GSH5}>{progressPercentage}%</Text>
        )}
        {!showPercentageOnly && (
          <Flex
            align="center"
            justify="space-between"
          >
            <div>
              <Text type={Typography.GSH5}>
                {hideValues
                  ? '****'
                  : formatToCurrency(String(goal.achievedAmount), '$')}
              </Text>
              {!hideValues && goal.amount && (
                <Text
                  type={Typography.GSBODY3}
                  color={Colors.MEDIUM_EMPHASIS}
                  style={paddingLeft}
                >
                  {`of ${formatToCurrency(String(goal.amount), '$')}`}
                </Text>
              )}
            </div>
            {singleCard && !isMobile && (
              <Button
                onClick={goToSelectCard}
                type={TypesButton.PRIMARY}
                style={giveToGoalBtnStyle}
              >
                {t('profile.profileDetailCard.giveToGoal')}
              </Button>
            )}
          </Flex>
        )}

        <Flex
          justify="space-between"
          align="center"
        >
          <div>
            <Text type={Typography.GSBODY3}>
              {t('profile.profileDetailCard.savedToward')}
            </Text>
            <Flex
              align="end"
              gap="4px"
            >
              <Icon
                icon={IconName.CALENDAR}
                color={Colors.MEDIUM_EMPHASIS}
                size={18}
              />
              <Text
                type={Typography.GSCAPTION}
                color={Colors.MEDIUM_EMPHASIS}
              >
                {date ? date : t('profile.profileDetailCard.noEndDate')}
              </Text>
            </Flex>
          </div>
          {singleCard && goal.publicAchievedAmount === 0 && (
            <Text
              type={Typography.GSCAPTION}
              style={fontWeight}
            >
              {t('profile.profileDetailCard.beTheFirst')}
            </Text>
          )}
        </Flex>
        {singleCard && isMobile && (
          <Button
            onClick={goToSelectCard}
            type={TypesButton.PRIMARY}
            style={giveToGoalBtnStyleMobile}
          >
            {t('profile.profileDetailCard.giveToGoal')}
          </Button>
        )}
      </Flex>
      {!singleCard && (
        <>
          <Separator />
          <Flex
            justify="space-between"
            align="center"
          >
            <div onClick={goToSelectCard}>
              <Text
                type={Typography.GSBUTTON}
                color={Colors.ORANGE}
                style={supportButtonStyle}
              >
                {t('profile.profileDetailCard.supportButtonLabel')}
              </Text>
            </div>
            {!hasInvestors && goal.publicAchievedAmount === 0 && (
              <Text type={Typography.GSCAPTION}>
                {t('profile.profileDetailCard.beTheFirst')}
              </Text>
            )}
          </Flex>
        </>
      )}
      {singleCard && (
        <Flex
          direction="column"
          style={bottomBannerStyle}
        >
          <Text type={Typography.GSCAPTION}>
            {t('profile.profileDetailCard.goalFacts')}
          </Text>
          <Text
            type={Typography.GSCAPTION}
            style={fontWeight}
          >
            {t('profile.profileDetailCard.goalFactsDescription')}
          </Text>
        </Flex>
      )}
    </Card>
  );
};
